// The vertical calendar rules behind the Productivity Chart bands.
//
// The chart draws no ordinary gridlines: at a year's width Highcharts' own tick grid is either too
// dense to read past or too sparse to measure against, and neither lands on a boundary a reader
// cares about. Rules on real calendar edges do, and they make a span skimmable - "this run is about
// three weeks" - without a single label.
//
// Cadence is chosen, never fixed. A rule every seven days is right for a three-month range and
// absurd at the wide end of the scale: over twelve months it is fifty-two verticals behind the
// data. So the coarsest cadence that stays under MAX_RULES wins. Chosen from the visible span
// alone - this cadence is a property of how much time is on screen, not of the bucket granularity.

import { DAY_MS, WEEK_MS } from './productivityFormatting';

export type CalendarRuleCadence = 'day' | 'week' | 'month';

// About as many verticals as a chart around a thousand pixels wide can carry before they stop
// reading as a grid and start reading as hatching over the series.
const MAX_RULES = 20;

// Only ever compared against MAX_RULES to pick a cadence, so the average month is precise enough -
// the boundaries themselves are stepped calendar-exactly below.
const AVERAGE_MONTH_MS = 30.44 * DAY_MS;

/**
 * Every calendar boundary of the given cadence in (fromMs, toMs), stepped in UTC.
 *
 * UTC because that is the clock this axis is in: every instant on it is a directory-local
 * wall-clock reading stamped as UTC (see `productivityInstant`). date-fns' `eachDayOfInterval` and friends
 * step in the *viewer's* zone, which put the whole grid a viewer's-offset away from the columns it
 * measures - so the same farm's grid sat in different places for a Danish and an American reader.
 * There is no date-fns UTC variant in this repo's dependency set, and the stepping is three lines,
 * so it is done here rather than adding one.
 *
 * Months step by calendar month rather than by a fixed span, so a month rule lands on the 1st
 * whatever the month's length; weeks step from the first Monday at or after `fromMs`, matching the
 * calendar-week numbering the axis labels use.
 */
function utcBoundaries(fromMs: number, toMs: number, cadence: CalendarRuleCadence): number[] {
    const from = new Date(fromMs);
    const boundaries: number[] = [];

    if (cadence === 'month') {
        // Start at the first of the month containing `from`, then walk months.
        let year = from.getUTCFullYear();
        let month = from.getUTCMonth();
        for (;;) {
            const value = Date.UTC(year, month, 1);
            if (value >= toMs) break;
            boundaries.push(value);
            month += 1;
            if (month > 11) {
                month = 0;
                year += 1;
            }
        }
        return boundaries;
    }

    const startOfDay = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate());
    if (cadence === 'day') {
        for (let value = startOfDay; value < toMs; value += DAY_MS) boundaries.push(value);
        return boundaries;
    }

    // Monday = 1 in getUTCDay()'s Sunday-based numbering, so this is how far back the containing
    // week's Monday is.
    const daysSinceMonday = (new Date(startOfDay).getUTCDay() + 6) % 7;
    for (let value = startOfDay - daysSinceMonday * DAY_MS; value < toMs; value += WEEK_MS) boundaries.push(value);
    return boundaries;
}

/**
 * The coarsest cadence whose rules stay under MAX_RULES across the span, falling back to months for
 * any range too long even for those - a decade of months is still more useful than nothing, and no
 * coarser calendar edge is worth marking on a chart that reads in weeks.
 */
export function chooseCalendarRuleCadence(spanMs: number): CalendarRuleCadence {
    if (spanMs / DAY_MS <= MAX_RULES) return 'day';
    if (spanMs / WEEK_MS <= MAX_RULES) return 'week';
    return 'month';
}

/**
 * Highcharts x-axis `plotLines` for every calendar boundary in [fromMs, toMs] at the cadence the
 * span earns. Weeks start Monday, matching the week numbering the axis labels already use.
 *
 * These are deliberately weaker than the Production Group boundary lines drawn alongside them: a period
 * boundary is where week-in-period numbering restarts, which changes what the axis labels *mean*,
 * while a calendar rule only helps the eye measure. Given equal weight the important one disappears
 * into the scaffolding.
 *
 * Stepped in UTC, because that is the clock this axis is in: every instant on it is a
 * directory-local wall-clock reading stamped as UTC (see `productivityInstant`). date-fns' interval helpers
 * step in the *viewer's* zone, which places the whole calendar grid a viewer's-offset away from the
 * columns it measures - two people looking at the same farm would see the grid in different places,
 * four or five hours apart for an American viewer of a European farm.
 */
export function buildCalendarRulePlotLines(fromMs: number, toMs: number, color: string): Array<Record<string, unknown>> {
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return [];

    const cadence = chooseCalendarRuleCadence(toMs - fromMs);
    const boundaries = utcBoundaries(fromMs, toMs, cadence);

    return boundaries
        .map((boundary) => boundary)
        // The first boundary commonly coincides with the axis minimum, where a rule is
        // indistinguishable from the plot's own left border and only thickens it.
        .filter((value) => value > fromMs && value < toMs)
        .map((value) => ({
            id: `productivity-calendar-rule-${value}`,
            value,
            color,
            width: 1,
            // Below the Production Group boundaries (3) and the reference-now marker (4), above the
            // plot bands the context bands paint at 0.
            zIndex: 1,
        }));
}
